"use client"

import React, { Suspense, useState } from "react"
import { useSearchParams } from "next/navigation"
import { useRouter } from "next/navigation"
import { useSession } from "next-auth/react"
import { Eye, EyeOff } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { ThemeToggle } from "@/components/ThemeToggle"
import { useTheme } from "@/components/ThemeProvider"

function SearchParamsError() {
  const params = useSearchParams()
  if (!params.get("error")) return null
  return <p className="text-sm text-red-400 text-center">Email ou mot de passe incorrect</p>
}

export default function LoginPage() {
  const { theme } = useTheme()
  const router = useRouter()
  const { update } = useSession()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        body: new FormData(e.currentTarget),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setError(
          body.error === "too_many_attempts"
            ? "Trop de tentatives. Réessayez dans 15 minutes."
            : "Email ou mot de passe incorrect"
        )
        setLoading(false)
        return
      }

      const { redirectTo } = await res.json()

      // update() appelle GET /api/auth/session — ce round-trip confirme que
      // le cookie session-token est bien dans le cookie store du navigateur
      // (iOS WKWebView a un délai entre Set-Cookie reçu et cookie disponible).
      await update()

      // router.push → history.pushState → navigation SPA → reste en standalone iOS.
      router.push(redirectTo)
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.")
      setLoading(false)
    }
  }

  return (
    <div className="login-page min-h-screen flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Lueur d'ambiance */}
      <div className="login-glow login-glow--blue" />
      <div className="login-glow login-glow--orange" />

      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-sm space-y-8 relative z-10">
        {/* Logo + titre */}
        <div className="flex flex-col items-center space-y-4">
          <Image
            src={
              theme === "dark"
                ? "/assets/LJS PLOMBERIE_logo_clair.png"
                : "/assets/LJSPLOMBERIE_logo.png"
            }
            alt="LJS Plomberie"
            width={220}
            height={100}
            className="object-contain drop-shadow-lg"
            priority
          />
          <p className="login-badge">Espace professionnel</p>
        </div>

        {/* Carte de connexion */}
        <div className="login-card">
          <div className="login-card__inner">
            <h1 className="login-title">Connexion</h1>

            <form suppressHydrationWarning onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="login-label" htmlFor="email">
                  Email
                </label>
                <input
                  suppressHydrationWarning
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="login-input"
                  placeholder="votre@email.fr"
                />
              </div>

              <div className="space-y-1.5">
                <label className="login-label" htmlFor="password">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    suppressHydrationWarning
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="login-input pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="login-eye-btn"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-red-400 text-center">{error}</p>}

              <Suspense>
                <SearchParamsError />
              </Suspense>

              <button type="submit" disabled={loading} className="login-btn">
                {loading ? "Connexion…" : "Se connecter"}
              </button>
            </form>
          </div>
        </div>

        <div className="text-center">
          <Link href="/" className="login-back-link">
            ← Retour à l&apos;accueil
          </Link>
        </div>
      </div>
    </div>
  )
}
